import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import type {
  AutoBlock,
  AutoBlockProject,
  CarriedAction,
  KpiTrendPoint,
} from '../../../lib/api/team-meetings.js';
import type { OrgIssuePeople } from '../../../lib/api/org-issues.js';
import { isOverdue, localToday, personLabel } from '../../../lib/org-issues.js';

/** 数値を小数 1 桁までで出す（目標が無ければ実績だけ）。 @graph-connects none */
function figure(actual: number | null, target: number | undefined): string {
  const text = actual === null ? '—' : String(Math.round(actual * 10) / 10);
  return target === undefined ? text : `${text} / ${String(Math.round(target * 10) / 10)}`;
}

/** KPI の最新の点（開催日までの期間の最後の日）。 @graph-connects none */
function KpiBlock({ points }: { points: readonly KpiTrendPoint[] }): ReactNode {
  const { t } = useTranslation();
  const latest = points[points.length - 1];
  if (latest === undefined) {
    return (
      <p className="text-xs text-[var(--color-text-muted)]">{t('teamMeetings.blocks.noData')}</p>
    );
  }
  return (
    <dl className="grid grid-cols-3 gap-2 text-sm">
      {(
        [
          ['bugRate', figure(latest.bugRate, undefined)],
          ['developPoint', figure(latest.developPointMtd, latest.developPointMtdTarget)],
          ['releaseCount', figure(latest.releaseCountMtd, latest.releaseCountMtdTarget)],
        ] as const
      ).map(([key, value]) => (
        <div key={key}>
          <dt className="text-xs text-[var(--color-text-muted)]">{t(`teamMeetings.kpi.${key}`)}</dt>
          <dd className="font-semibold text-[var(--color-text-primary)]">{value}</dd>
        </div>
      ))}
      <p className="col-span-3 text-xs text-[var(--color-text-muted)]">
        {t('teamMeetings.kpi.asOf', { date: latest.date })}
      </p>
    </dl>
  );
}

/** 案件の一覧。 @graph-connects none */
function ProjectList({ projects }: { projects: readonly AutoBlockProject[] }): ReactNode {
  const { t } = useTranslation();
  if (projects.length === 0) {
    return (
      <p className="text-xs text-[var(--color-text-muted)]">{t('teamMeetings.blocks.none')}</p>
    );
  }
  return (
    <ul className="space-y-1 text-sm">
      {projects.map((p) => (
        <li key={p.issueKey} className="flex flex-wrap gap-x-2">
          <span className="text-[var(--color-text-muted)]">{p.issueKey}</span>
          <span className="text-[var(--color-text-primary)]">{p.summary}</span>
          <span className="text-xs text-[var(--color-text-muted)]">
            {p.dueDate ?? t('teamMeetings.blocks.noDue')} / {p.assignee ?? '—'}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** ブロック 1 つの中身。 @graph-connects none */
function BlockBody({ block, people }: { block: AutoBlock; people: OrgIssuePeople }): ReactNode {
  const { t } = useTranslation();
  if (block.status !== 'ok') {
    return (
      <p
        className={`text-xs ${block.status === 'error' ? 'text-[var(--color-error)]' : 'text-[var(--color-text-muted)]'}`}
      >
        {block.reason}
      </p>
    );
  }
  switch (block.key) {
    case 'kpi':
      return <KpiBlock points={block.trend.data} />;
    case 'holiday_ops':
      return block.covers.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">{t('teamMeetings.blocks.none')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {block.covers.map((c) => (
            <li key={`${c.date}/${c.service}`}>
              {/* 曜日は API が日付から計算したものをそのまま出す（画面で写さない） */}
              {c.date}({c.weekday}) {c.serviceLabel}:{' '}
              {/* 表示名が引けなければ、users.id（数字）ではなく元の担当者名を出す */}
              {c.userId !== null && people[c.userId] !== undefined
                ? personLabel(people, c.userId)
                : c.who}
            </li>
          ))}
        </ul>
      );
    case 'active_projects':
    case 'upcoming_releases':
      return <ProjectList projects={block.projects} />;
    case 'released_projects':
      return block.releases.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">{t('teamMeetings.blocks.none')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {block.releases.map((r) => (
            <li key={r.issueKey} className="flex flex-wrap gap-x-2">
              <span className="text-[var(--color-text-muted)]">{r.releasedDate}</span>
              <span className="text-[var(--color-text-muted)]">{r.issueKey}</span>
              <span className="text-[var(--color-text-primary)]">{r.summary}</span>
            </li>
          ))}
        </ul>
      );
  }
}

/**
 * 開催回の上部に出す定例の自動ブロック。並びと中身は API が返したまま出し、出せない・失敗したブロックは
 * API の理由をその場所に出す。**読み込み自体に失敗しても開催回の他の表示は止めない。**
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の開催回の上部に KPI・休日オペ担・案件の自動ブロックを並べ、出せないブロックは理由を出す
 * @graph-connects none
 */
export function AutoBlocksPanel({
  blocks,
  isLoading,
  isError,
  people,
}: {
  blocks: readonly AutoBlock[] | undefined;
  isLoading: boolean;
  isError: boolean;
  people: OrgIssuePeople;
}): ReactNode {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('teamMeetings.blocks.loading')}</p>
    );
  }
  if (isError || blocks === undefined) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('teamMeetings.blocks.loadError')}</p>
    );
  }
  if (blocks.length === 0) return null;
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {blocks.map((block) => (
        <Card key={block.key}>
          <CardContent className="space-y-2 p-4">
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t(`teamMeetings.blocks.${block.key}`)}
            </h3>
            <BlockBody block={block} people={people} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * 持ち越しの Next（既定のボードの閉じていない課題の未完了 Next）。並びは API の返した順（期日の近い順）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の開催回に、既定のボードから持ち越した未完了の Next を担当・期日つきで並べ、課題を開く入口にする
 * @graph-connects isOverdue [calls] 期日超過の判定
 */
export function CarriedActions({
  boardId,
  actions,
  people,
}: {
  boardId: string | null;
  actions: readonly CarriedAction[];
  people: OrgIssuePeople;
}): ReactNode {
  const { t } = useTranslation();
  const today = localToday();
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('teamMeetings.carried.heading')}
        </h2>
        {boardId === null && (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('teamMeetings.carried.noBoard')}
          </p>
        )}
        {boardId !== null && actions.length === 0 && (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('teamMeetings.carried.empty')}
          </p>
        )}
        <ul className="divide-y divide-[var(--color-border)]">
          {actions.map(({ issue, action }) => (
            <li key={`${issue.id}/${action.id}`} className="py-2 text-sm">
              {boardId === null ? (
                action.text
              ) : (
                <Link
                  to="/org-issues/$boardId"
                  params={{ boardId }}
                  search={{ issue: issue.id }}
                  className="text-[var(--color-text-primary)] hover:opacity-80"
                >
                  {action.text}
                </Link>
              )}
              <p className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
                <span>{issue.title}</span>
                <span>
                  {action.assigneeIds.map((id) => personLabel(people, id)).join(', ') ||
                    t('teamMeetings.carried.noAssignee')}
                </span>
                <span>{action.dueOn ?? t('teamMeetings.blocks.noDue')}</span>
                {isOverdue(action.dueOn, today) && (
                  <Badge variant="error" size="sm">
                    {t('teamMeetings.carried.overdue')}
                  </Badge>
                )}
              </p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
